import { useCallback, useEffect, useMemo, useState } from "react";
import { getAllComplaintsURL, getDeleteComplaintURL, getUpdateComplaintURL } from "../../../libraries/apiUrl";
import { getAdminUser } from "../../../libraries/auth";
import { _delete, _post, _put, _get } from "../../../libraries/fetch";

const STATUS_FILTERS = [
  { id: "pending", label: "Pending" },
  { id: "inprogress", label: "In-Progress" },
  { id: "solved", label: "Solved" },
  { id: "closed", label: "Closed" }
];

const UPDATE_STATUS_OPTIONS = [
  { id: "pending", label: "Pending" },
  { id: "inprogress", label: "In Progress" },
  { id: "solved", label: "Resolved" },
  { id: "closed", label: "Closed" }
];

const ENGINEER_OPTIONS = [
  "Jari Abbas",
  "Masood Ahmed",
  "Waqas Ali Hasnain",
  "Muhammed Adeel",
  "Asad Ullah Mujahid",
  "Sohaib Ali",
  "Ahmed"
];

function ClientQueries({ companyId, branchId }) {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedRemarks, setSelectedRemarks] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [selectedUpdateQuery, setSelectedUpdateQuery] = useState(null);
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateError, setUpdateError] = useState("");
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [updateForm, setUpdateForm] = useState({
    engineerName: "",
    status: "Pending",
    remark: ""
  });

  const fetchQueries = useCallback(async () => {
    if (!branchId) {
      setQueries([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await _get(getAllComplaintsURL(), { useCache: false });

      if (response?.success === false) {
        throw new Error(response.message || "Failed to load client queries.");
      }

      setQueries(extractQueryItems(response));
    } catch (err) {
      setError(err?.message || "Failed to load client queries. Please try again.");
      setQueries([]);
    } finally {
      setLoading(false);
    }
  }, [branchId]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const rows = useMemo(
    () => queries
      .filter((query) => {
        const queryBranchId = getBranchId(query);
        return !queryBranchId || String(queryBranchId) === String(branchId);
      })
      .map(normalizeQuery)
      .filter((query) => matchesQueryStatus(query.status, selectedStatus)),
    [branchId, queries, selectedStatus]
  );

  const selectedStatusLabel = STATUS_FILTERS.find((filter) => filter.id === selectedStatus)?.label || "Pending";

  const handleOpenUpdatePopup = (query) => {
    setSelectedUpdateQuery(query);
    setUpdateError("");
    setUpdateForm({
      engineerName: ENGINEER_OPTIONS.includes(query.engineerName) ? query.engineerName : "",
      status: getStatusLabel(query.status),
      remark: ""
    });
  };

  const handleCloseUpdatePopup = () => {
    setSelectedUpdateQuery(null);
    setUpdateSubmitting(false);
    setUpdateError("");
    setUpdateForm({
      engineerName: "",
      status: "Pending",
      remark: ""
    });
  };

  const handleUpdateFieldChange = (field, value) => {
    setUpdateForm((currentForm) => ({
      ...currentForm,
      [field]: value
    }));
  };

  const handleUpdateComplaint = (event) => {
    event.preventDefault();

    if (!selectedUpdateQuery) {
      return;
    }

    const engineerName = updateForm.engineerName.trim();
    const status = updateForm.status.trim();
    const comment = buildUpdatedComment(selectedUpdateQuery.remarksHistoryItems, updateForm.remark);

    if (!selectedUpdateQuery.id || selectedUpdateQuery.id === "N/A") {
      setUpdateError("Complaint id is missing. Please refresh and try again.");
      return;
    }

    if (!engineerName) {
      setUpdateError("Engineer name is required.");
      return;
    }

    if (!status) {
      setUpdateError("Status is required.");
      return;
    }

    if (!comment) {
      setUpdateError("Please add your remark.");
      return;
    }

    setConfirmDialog({
      type: "update",
      title: "Confirm Update",
      message: "Are you sure you want to update this complaint?",
      confirmLabel: "Yes",
      cancelLabel: "No",
      variant: "success",
      query: selectedUpdateQuery,
      payload: buildUpdatePayload(selectedUpdateQuery.id, engineerName, status, comment),
      form: { engineerName, status, remark: updateForm.remark },
      comment
    });
  };

  const handleDeleteComplaint = (query) => {
    if (!query.id || query.id === "N/A") {
      setError("Complaint id is missing. Please refresh and try again.");
      return;
    }

    setConfirmDialog({
      type: "delete",
      title: "Confirm Delete",
      message: `Are you sure you want to delete complaint ${query.ticketNumber || ""}?`,
      confirmLabel: "Yes",
      cancelLabel: "No",
      variant: "danger",
      query
    });
  };

  const handleCloseConfirmDialog = () => {
    if (!updateSubmitting) {
      setConfirmDialog(null);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmDialog) {
      return;
    }

    if (confirmDialog.type === "update") {
      await executeComplaintUpdate(confirmDialog);
      return;
    }

    if (confirmDialog.type === "delete") {
      await executeComplaintDelete(confirmDialog.query);
    }
  };

  const executeComplaintUpdate = async ({ payload, query, form, comment }) => {
    setUpdateSubmitting(true);
    setUpdateError("");
    setConfirmDialog(null);

    try {
      const response = await _put(getUpdateComplaintURL(), payload);

      if (response?.success === false) {
        throw new Error(response.message || "Failed to update complaint.");
      }

      setQueries((currentQueries) => currentQueries.map((currentQuery) => {
        if (!isMatchingQuery(currentQuery, query)) {
          return currentQuery;
        }

        return applyQueryUpdate(currentQuery, form, comment);
      }));
      setConfirmDialog(null);
      await fetchQueries();
      handleCloseUpdatePopup();
    } catch (err) {
      setUpdateError(err?.message || "Failed to update complaint. Please try again.");
      setUpdateSubmitting(false);
    }
  };

  const executeComplaintDelete = async (query) => {
    setUpdateSubmitting(true);
    setError("");
    setConfirmDialog(null);

    try {
      const response = await _delete(getDeleteComplaintURL(query.id));

      if (response?.success === false) {
        throw new Error(response.message || "Failed to delete complaint.");
      }

      setQueries((currentQueries) => currentQueries.filter((currentQuery) => !isMatchingQuery(currentQuery, query)));
      setConfirmDialog(null);
      await fetchQueries();
    } catch (err) {
      setError(err?.message || "Failed to delete complaint. Please try again.");
    } finally {
      setUpdateSubmitting(false);
    }
  };

  return (
    <div style={panelStyle}>
      <div style={headerRowStyle}>
        <div>
          <h2 style={headingStyle}>Clients Queries</h2>
          <div style={statusFilterGroupStyle} aria-label="Query status filter">
            {STATUS_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setSelectedStatus(filter.id)}
                style={getStatusFilterButtonStyle(selectedStatus === filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <p style={subtitleStyle}>{selectedStatusLabel} Clients Queries</p>
        </div>
        <div style={headerActionsStyle}>
          <button type="button" onClick={fetchQueries} disabled={loading || !branchId} style={refreshButtonStyle}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={emptyStyle}>Loading client queries...</div>
      ) : error ? (
        <div style={errorStyle}>{error}</div>
      ) : rows.length === 0 ? (
        <div style={emptyStyle}>No {selectedStatusLabel.toLowerCase()} queries found for this client.</div>
      ) : (
        <div style={tableShellStyle}>
          <table style={tableStyle}>
            <thead>
              <tr style={tableHeadRowStyle}>
                <th style={tableHeaderStyle}>Serial No</th>
                <th style={tableHeaderStyle}>Ticket Number</th>
                <th style={tableHeaderStyle}>Complainant Name</th>
                <th style={tableHeaderStyle}>Query</th>
                <th style={tableHeaderStyle}>Query Type</th>
                <th style={tableHeaderStyle}>Query Date</th>
                <th style={tableHeaderStyle}>Query Status</th>
                <th style={tableHeaderStyle}>Engineer Name</th>
                <th style={tableHeaderStyle}>Remarks History</th>
                <th style={tableHeaderStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((query, index) => (
                <tr
                  key={query.id || `${query.ticketNumber}-${query.message}-${index}`}
                  style={{
                    borderBottom: "1px solid #333",
                    backgroundColor: index % 2 === 0 ? "#1a1d23" : "#20232b"
                  }}
                >
                  <td style={tableCellStyle}>{index + 1}</td>
                  <td style={tableCellStyle}>{query.ticketNumber}</td>
                  <td style={tableCellStyle}>{query.complainantName}</td>
                  <td style={queryCellStyle}>{query.message}</td>
                  <td style={tableCellStyle}>{query.queryType}</td>
                  <td style={tableCellStyle}>{formatDate(query.date)}</td>
                  <td style={tableCellStyle}>
                    <span style={statusStyle}>{query.status}</span>
                  </td>
                  <td style={tableCellStyle}>{query.engineerName}</td>
                  <td style={tableCellStyle}>
                    <button
                      type="button"
                      onClick={() => setSelectedRemarks(query)}
                      style={chatIconButtonStyle}
                      title="View remarks history"
                      aria-label="View remarks history"
                    >
                      <svg viewBox="0 0 24 24" aria-hidden="true" style={chatIconSvgStyle}>
                        <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"></path>
                        <line x1="8" y1="9" x2="16" y2="9"></line>
                        <line x1="8" y1="13" x2="14" y2="13"></line>
                      </svg>
                    </button>
                  </td>
                  <td style={tableCellStyle}>
                    <button
                      type="button"
                      onClick={() => (isResolvedStatus(query.status) ? handleDeleteComplaint(query) : handleOpenUpdatePopup(query))}
                      disabled={updateSubmitting}
                      style={isResolvedStatus(query.status) ? deleteButtonStyle : updateButtonStyle}
                    >
                      {isResolvedStatus(query.status) ? "Delete" : "Update"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedRemarks && (
        <div style={popupOverlayStyle} role="dialog" aria-modal="true" aria-label="Remarks history">
          <div style={popupStyle}>
            <div style={popupHeaderStyle}>
              <div>
                <h3 style={popupTitleStyle}>Remarks History</h3>
                <p style={popupSubtitleStyle}>Ticket: {selectedRemarks.ticketNumber}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRemarks(null)}
                style={closeButtonStyle}
                aria-label="Close remarks history"
              >
                X
              </button>
            </div>
            <div style={remarksListStyle}>
              {selectedRemarks.remarksHistoryItems.length === 0 ? (
                <div style={emptyRemarksStyle}>No remarks found.</div>
              ) : (
                selectedRemarks.remarksHistoryItems.map((remark, index) => (
                  <div key={`${remark}-${index}`} style={remarkItemStyle}>
                    {remark}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {selectedUpdateQuery && (
        <div style={updatePopupOverlayStyle} role="dialog" aria-modal="true" aria-label="Update complaint">
          <form style={updatePopupStyle} onSubmit={handleUpdateComplaint}>
            <div style={updatePopupHeaderStyle}>
              <div style={updatePopupHeadingStyle}>
                <div style={updatePopupIconStyle}>
                  <svg viewBox="0 0 24 24" aria-hidden="true" style={updatePopupHeaderIconStyle}>
                    <path d="M4 5h16v10H7l-3 3V5z"></path>
                    <path d="M8 9h8"></path>
                    <path d="M8 12h5"></path>
                  </svg>
                </div>
                <div>
                  <h3 style={updatePopupTitleStyle}>Update Complaint</h3>
                  <p style={updatePopupTicketStyle}>Ticket: {selectedUpdateQuery.ticketNumber}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseUpdatePopup}
                style={updatePopupCloseButtonStyle}
                aria-label="Close update complaint"
              >
                X
              </button>
            </div>

            <div style={updateFieldRowStyle}>
              <label style={updateFieldGroupStyle}>
                <span style={updateLabelStyle}>Engineer Name</span>
                <select
                  value={updateForm.engineerName}
                  onChange={(event) => handleUpdateFieldChange("engineerName", event.target.value)}
                  style={updateSelectStyle}
                >
                  <option value="">Select engineer</option>
                  {ENGINEER_OPTIONS.map((engineerName) => (
                    <option key={engineerName} value={engineerName}>
                      {engineerName}
                    </option>
                  ))}
                </select>
              </label>

              <label style={updateFieldGroupStyle}>
                <span style={updateLabelStyle}>Status</span>
                <select
                  value={updateForm.status}
                  onChange={(event) => handleUpdateFieldChange("status", event.target.value)}
                  style={updateSelectStyle}
                >
                  {UPDATE_STATUS_OPTIONS.map((status) => (
                    <option key={status.id} value={status.label}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label style={updateTextareaGroupStyle}>
              <span style={updateLabelStyle}>Remarks History</span>
              <textarea
                value={selectedUpdateQuery.remarksHistoryItems.join("\n")}
                readOnly
                placeholder="Add remarks"
                style={updateReadonlyTextareaStyle}
              />
            </label>

            <label style={updateTextareaGroupStyle}>
              <span style={updateLabelStyle}>Add Your Remark</span>
              <textarea
                value={updateForm.remark}
                onChange={(event) => handleUpdateFieldChange("remark", event.target.value)}
                placeholder="Add your remark"
                style={updateTextareaStyle}
              />
            </label>

            {updateError && <div style={updatePopupErrorStyle}>{updateError}</div>}

            <button
              type="submit"
              disabled={updateSubmitting}
              style={{
                ...updatePopupSubmitButtonStyle,
                cursor: updateSubmitting ? "not-allowed" : "pointer",
                opacity: updateSubmitting ? 0.72 : 1
              }}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" style={updateSubmitIconStyle}>
                <circle cx="12" cy="12" r="9"></circle>
                <path d="m8 12 2.5 2.5L16 9"></path>
              </svg>
              {updateSubmitting ? "Updating..." : "Update"}
            </button>
          </form>
        </div>
      )}

      {confirmDialog && (
        <div style={confirmOverlayStyle} role="dialog" aria-modal="true" aria-label={confirmDialog.title}>
          <div style={confirmCardStyle}>
            <div style={confirmIconStyle(confirmDialog.variant)}>
              {confirmDialog.variant === "danger" ? "!" : "?"}
            </div>
            <h3 style={confirmTitleStyle}>{confirmDialog.title}</h3>
            <p style={confirmMessageStyle}>{confirmDialog.message}</p>
            <div style={confirmActionsStyle}>
              <button
                type="button"
                onClick={handleCloseConfirmDialog}
                disabled={updateSubmitting}
                style={confirmNoButtonStyle}
              >
                {confirmDialog.cancelLabel}
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                disabled={updateSubmitting}
                style={confirmYesButtonStyle(confirmDialog.variant)}
              >
                {updateSubmitting ? "Please wait..." : confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function extractQueryItems(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const listKeys = [
    "queries",
    "query",
    "complaints",
    "complaint",
    "response",
    "data",
    "result",
    "results",
    "records",
    "items",
    "docs"
  ];

  for (const key of listKeys) {
    const value = response[key];

    if (Array.isArray(value)) {
      return value;
    }

    const nestedItems = extractQueryItems(value);
    if (nestedItems.length > 0) {
      return nestedItems;
    }
  }

  return [];
}

function normalizeQuery(query) {
  return {
    id: getValue(query, ["_id", "id"]),
    ticketNumber: formatValue(getValue(query, ["ticketNumber", "ticketNo", "ticketId", "complaintNo", "complaintNumber", "complaintId", "number"])),
    complainantName: formatValue(getValue(query, ["complainantName", "complainentName", "complaintBy", "complainant", "createdBy", "name", "clientName", "customerName", "memberName"])),
    date: getValue(query, ["createdAt", "created_at", "createdDate", "complaintDate", "queryDate", "date", "submittedAt", "updatedAt"]),
    message: formatValue(getValue(query, ["message", "complaint", "complaintText", "complaintDescription", "query", "question", "description", "details", "remarks", "comment", "subject"])),
    queryType: formatValue(getValue(query, ["queryType", "complaintType", "type", "category", "issueType", "ticketType"])),
    engineerName: formatValue(getValue(query, ["engineerName", "enginerName", "engineer", "assignedEngineer", "assignedTo", "technicianName", "supportName"])),
    status: formatValue(getValue(query, ["status", "complaintStatus", "queryStatus", "state"]) || "Pending"),
    remarksHistoryItems: formatRemarksHistoryItems(getValue(query, ["comment", "remarksHistory", "remarkHistory", "remarks", "comments", "history", "statusHistory", "activityHistory"]))
  };
}

function matchesQueryStatus(status, selectedStatus) {
  const statusKey = getQueryStatusKey(status);
  const statusAliases = {
    pending: ["pending"],
    inprogress: ["inprogress", "inprocess", "processing", "progress", "ongoing"],
    solved: ["solved", "resolved", "resolve", "completed", "complete", "done"],
    closed: ["closed", "close"]
  };

  return (statusAliases[selectedStatus] || [selectedStatus]).includes(statusKey);
}

function getQueryStatusKey(status) {
  return String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
}

function getStatusLabel(status) {
  return UPDATE_STATUS_OPTIONS.find((filter) => matchesQueryStatus(status, filter.id))?.label || "Pending";
}

function isResolvedStatus(status) {
  return matchesQueryStatus(status, "solved");
}

function buildUpdatePayload(id, engineerName, status, comment) {
  const loggedInUser = getAdminUser();
  const activityBy = (loggedInUser?.name || loggedInUser?.userName || loggedInUser?.username || "").trim();

  return {
    engineerName,
    status,
    comment,
    activityBy: activityBy || null,
    _id: id
  };
}

function isMatchingQuery(query, selectedQuery) {
  const queryId = getValue(query, ["_id", "id"]);
  const selectedId = selectedQuery.id;

  if (queryId && selectedId) {
    return String(queryId) === String(selectedId);
  }

  const ticketNumber = formatValue(getValue(query, ["ticketNumber", "ticketNo", "ticketId", "complaintNo", "complaintNumber", "complaintId", "number"]));
  return ticketNumber === selectedQuery.ticketNumber;
}

function applyQueryUpdate(query, form, comment) {
  const statusKey = getExistingKey(query, ["status", "complaintStatus", "queryStatus", "state"], "status");
  const engineerKey = getExistingKey(query, ["engineerName", "enginerName", "engineer", "assignedEngineer", "assignedTo", "technicianName", "supportName"], "engineerName");
  const nextQuery = {
    ...query,
    [statusKey]: form.status,
    [engineerKey]: form.engineerName || "N/A",
    comment
  };

  delete nextQuery.remarksHistory;
  delete nextQuery.remarkHistory;
  delete nextQuery.comments;

  return nextQuery;
}

function buildUpdatedComment(historyItems, newRemark) {
  const historyText = (historyItems || []).join("\n\n").trim();
  const parts = parseNumberedRemarksFromDisplay(historyText);
  const latest = (newRemark || "").trim();

  if (latest) {
    parts.push(latest);
  }

  if (parts.length > 0) {
    return formatRemarksWithSerial(parts);
  }

  return historyText;
}

function parseNumberedRemarksFromDisplay(display) {
  const value = String(display || "").trim();

  if (!value) {
    return [];
  }

  const chunks = value
    .split(/\n\n(?=\d+\.\s)/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  if (chunks.length > 1 || /^\d+\.\s/.test(chunks[0] || "")) {
    return chunks.map((chunk) => chunk.replace(/^\d+\.\s*/, "").trim()).filter(Boolean);
  }

  return [value];
}

function formatRemarksWithSerial(parts) {
  return parts
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .map((part, index) => `${index + 1}. ${part}`)
    .join("\n\n");
}

function getExistingKey(source, keys, fallback) {
  if (!source || typeof source !== "object") {
    return fallback;
  }

  return keys.find((key) => source[key] !== undefined) || fallback;
}

function getBranchId(query) {
  const branchValue = getValue(query, ["branchId", "branch", "branch_id", "gymBranchId", "clientBranchId"]);

  if (branchValue && typeof branchValue === "object") {
    return branchValue._id || branchValue.id || "";
  }

  return branchValue;
}

function getValue(source, keys) {
  if (!source || typeof source !== "object") {
    return "";
  }

  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      return source[key];
    }
  }

  return "";
}

function formatValue(value) {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (Array.isArray(value)) {
    return value.map(formatValue).join(", ");
  }

  if (typeof value === "object") {
    return value.name || value.title || value.label || value._id || value.id || JSON.stringify(value);
  }

  return String(value);
}

function formatRemarksHistoryItems(value) {
  if (value === undefined || value === null || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [];
    }

    return value.map(formatRemarkItem);
  }

  return [formatValue(value)];
}

function formatRemarkItem(item) {
  if (!item || typeof item !== "object") {
    return formatValue(item);
  }

  const remark = item.remark || item.remarks || item.comment || item.message || item.description || item.text || "";
  const by = item.by || item.user || item.name || item.createdBy || item.updatedBy || "";
  const date = item.date || item.createdAt || item.updatedAt || "";
  const prefix = [formatDate(date), formatValue(by)]
    .filter((part) => part && part !== "N/A")
    .join(" - ");

  return prefix ? `${prefix}: ${formatValue(remark)}` : formatValue(remark || item);
}

function formatDate(dateString) {
  if (!dateString) {
    return "N/A";
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return formatValue(dateString);
  }

  return date.toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
}

const panelStyle = {
  padding: "24px",
  backgroundColor: "#1a1d23",
  borderRadius: "12px",
  border: "1px solid #3a3f4b"
};

const headerRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "center",
  marginBottom: "20px"
};

const headingStyle = {
  margin: "0 0 8px 0",
  color: "#fff",
  fontSize: "24px",
  fontWeight: 700
};

const subtitleStyle = {
  margin: 0,
  color: "#9ca3af",
  fontSize: "14px"
};

const headerActionsStyle = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flexWrap: "wrap",
  justifyContent: "flex-end"
};

const statusFilterGroupStyle = {
  display: "flex",
  gap: "6px",
  flexWrap: "wrap",
  margin: "8px 0"
};

function getStatusFilterButtonStyle(isActive) {
  return {
    padding: "8px 12px",
    fontSize: "13px",
    fontWeight: 700,
    backgroundColor: isActive ? "#187718" : "#2a2d35",
    color: isActive ? "#fff" : "#d1d5db",
    border: isActive ? "1px solid #187718" : "1px solid #3a3f4b",
    borderRadius: "6px",
    cursor: "pointer"
  };
}

const refreshButtonStyle = {
  padding: "10px 18px",
  fontSize: "14px",
  fontWeight: 600,
  backgroundColor: "#187718",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer"
};

const tableShellStyle = {
  overflowX: "auto",
  borderRadius: "10px",
  border: "1px solid #333"
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "14px"
};

const tableHeadRowStyle = {
  backgroundColor: "#2a2d35",
  borderBottom: "2px solid #187718"
};

const tableHeaderStyle = {
  padding: "14px 16px",
  textAlign: "left",
  fontWeight: 600,
  color: "#4ade80",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  fontSize: "12px",
  whiteSpace: "nowrap"
};

const tableCellStyle = {
  padding: "8px 12px",
  color: "#fff",
  verticalAlign: "middle"
};

const queryCellStyle = {
  ...tableCellStyle,
  minWidth: "280px",
  lineHeight: 1.5
};

const statusStyle = {
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "4px",
  backgroundColor: "rgba(24, 119, 24, 0.2)",
  color: "#4ade80",
  fontSize: "13px",
  fontWeight: 600
};

const chatIconButtonStyle = {
  width: "30px",
  height: "30px",
  padding: 0,
  border: "1px solid rgba(74, 222, 128, 0.45)",
  borderRadius: "50%",
  backgroundColor: "rgba(24, 119, 24, 0.18)",
  color: "#4ade80",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center"
};

const chatIconSvgStyle = {
  width: "16px",
  height: "16px",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};

const popupOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  backgroundColor: "rgba(0, 0, 0, 0.68)"
};

const popupStyle = {
  width: "min(680px, 100%)",
  maxHeight: "80vh",
  overflow: "hidden",
  border: "1px solid #3a3f4b",
  borderRadius: "14px",
  backgroundColor: "#1a1d23",
  boxShadow: "0 24px 70px rgba(0, 0, 0, 0.5)"
};

const popupHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  alignItems: "flex-start",
  padding: "20px 22px",
  borderBottom: "1px solid #333",
  backgroundColor: "#20232b"
};

const popupTitleStyle = {
  margin: "0 0 6px",
  color: "#fff",
  fontSize: "20px",
  fontWeight: 700
};

const popupSubtitleStyle = {
  margin: 0,
  color: "#9ca3af",
  fontSize: "13px"
};

const closeButtonStyle = {
  width: "34px",
  height: "34px",
  border: "1px solid #444",
  borderRadius: "50%",
  backgroundColor: "#1a1d23",
  color: "#fff",
  fontSize: "24px",
  lineHeight: "28px",
  cursor: "pointer"
};

const remarksListStyle = {
  maxHeight: "55vh",
  overflowY: "auto",
  padding: "18px 22px"
};

const remarkItemStyle = {
  marginBottom: "12px",
  padding: "12px 14px",
  border: "1px solid #333",
  borderRadius: "10px",
  backgroundColor: "#20232b",
  color: "#fff",
  lineHeight: 1.5,
  whiteSpace: "pre-wrap"
};

const emptyRemarksStyle = {
  padding: "24px",
  textAlign: "center",
  color: "#9ca3af"
};

const updateButtonStyle = {
  padding: "6px 10px",
  border: "none",
  borderRadius: "6px",
  backgroundColor: "#187718",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 700,
  cursor: "pointer"
};

const deleteButtonStyle = {
  ...updateButtonStyle,
  backgroundColor: "#dc2626"
};

const confirmOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  backgroundColor: "rgba(15, 23, 42, 0.68)",
  backdropFilter: "blur(4px)"
};

const confirmCardStyle = {
  width: "min(360px, 100%)",
  padding: "26px",
  borderRadius: "18px",
  backgroundColor: "#fff",
  color: "#111827",
  textAlign: "center",
  boxShadow: "0 24px 70px rgba(0, 0, 0, 0.42)"
};

function confirmIconStyle(variant) {
  const isDanger = variant === "danger";

  return {
    width: "52px",
    height: "52px",
    margin: "0 auto 14px",
    borderRadius: "50%",
    backgroundColor: isDanger ? "#fee2e2" : "#dcfce7",
    color: isDanger ? "#dc2626" : "#187718",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    fontWeight: 900
  };
}

const confirmTitleStyle = {
  margin: "0 0 8px",
  color: "#111827",
  fontSize: "21px",
  fontWeight: 800
};

const confirmMessageStyle = {
  margin: "0 0 24px",
  color: "#64748b",
  fontSize: "14px",
  lineHeight: 1.5
};

const confirmActionsStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "12px"
};

const confirmNoButtonStyle = {
  height: "42px",
  border: "1px solid #d8dee8",
  borderRadius: "12px",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: "14px",
  fontWeight: 800,
  cursor: "pointer"
};

function confirmYesButtonStyle(variant) {
  return {
    height: "42px",
    border: "none",
    borderRadius: "12px",
    backgroundColor: variant === "danger" ? "#dc2626" : "#187718",
    color: "#fff",
    fontSize: "14px",
    fontWeight: 800,
    cursor: "pointer"
  };
}

const updatePopupOverlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1100,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "18px",
  background: "radial-gradient(circle at top, rgba(74, 222, 128, 0.16), rgba(0, 0, 0, 0.72))",
  backdropFilter: "blur(5px)"
};

const updatePopupStyle = {
  width: "min(430px, 100%)",
  padding: "24px",
  border: "1px solid rgba(74, 222, 128, 0.22)",
  borderRadius: "20px",
  background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
  color: "#111827",
  boxShadow: "0 28px 80px rgba(0, 0, 0, 0.42), 0 0 0 1px rgba(255, 255, 255, 0.8)"
};

const updatePopupHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "22px",
  paddingBottom: "18px",
  borderBottom: "1px solid #e5e7eb"
};

const updatePopupHeadingStyle = {
  display: "flex",
  alignItems: "center",
  gap: "12px"
};

const updatePopupIconStyle = {
  width: "44px",
  height: "44px",
  borderRadius: "14px",
  background: "linear-gradient(135deg, #187718 0%, #4ade80 100%)",
  color: "#fff",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 10px 20px rgba(24, 119, 24, 0.25)"
};

const updatePopupHeaderIconStyle = {
  width: "24px",
  height: "24px",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};

const updatePopupTitleStyle = {
  margin: 0,
  color: "#111827",
  fontSize: "21px",
  fontWeight: 800,
  letterSpacing: "-0.02em"
};

const updatePopupTicketStyle = {
  margin: "4px 0 0",
  color: "#64748b",
  fontSize: "13px",
  fontWeight: 600
};

const updatePopupCloseButtonStyle = {
  width: "34px",
  height: "34px",
  padding: 0,
  border: "1px solid #e5e7eb",
  borderRadius: "50%",
  backgroundColor: "#fff",
  color: "#64748b",
  fontSize: "20px",
  lineHeight: "20px",
  cursor: "pointer",
  boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)"
};

const updateFieldRowStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 160px), 1fr))",
  gap: "12px",
  marginBottom: "16px"
};

const updateFieldGroupStyle = {
  display: "flex",
  flexDirection: "column"
};

const updateLabelStyle = {
  marginBottom: "8px",
  color: "#0f172a",
  fontSize: "13px",
  fontWeight: 800,
  letterSpacing: "0.01em",
  lineHeight: 1.2
};

const updateSelectStyle = {
  width: "100%",
  height: "44px",
  padding: "0 14px",
  border: "1px solid #d8dee8",
  borderRadius: "12px",
  backgroundColor: "#fff",
  color: "#334155",
  fontSize: "14px",
  fontWeight: 600,
  outline: "none",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
};

const updateTextareaGroupStyle = {
  display: "flex",
  flexDirection: "column",
  marginBottom: "16px"
};

const updateTextareaStyle = {
  width: "100%",
  minHeight: "104px",
  padding: "12px 14px",
  border: "1px solid #d8dee8",
  borderRadius: "14px",
  backgroundColor: "#fff",
  color: "#111827",
  fontSize: "14px",
  lineHeight: 1.5,
  resize: "vertical",
  outline: "none",
  boxSizing: "border-box",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
};

const updateReadonlyTextareaStyle = {
  ...updateTextareaStyle,
  backgroundColor: "#f8fafc",
  color: "#475569"
};

const updatePopupSubmitButtonStyle = {
  width: "100%",
  height: "48px",
  marginTop: "8px",
  border: "none",
  borderRadius: "14px",
  background: "linear-gradient(135deg, #187718 0%, #35b653 100%)",
  color: "#fff",
  fontSize: "15px",
  fontWeight: 800,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "12px",
  boxShadow: "0 12px 26px rgba(24, 119, 24, 0.28)"
};

const updatePopupErrorStyle = {
  margin: "0 0 12px",
  padding: "10px 12px",
  border: "1px solid rgba(220, 38, 38, 0.24)",
  borderRadius: "12px",
  backgroundColor: "rgba(254, 226, 226, 0.85)",
  color: "#b91c1c",
  fontSize: "13px",
  fontWeight: 700,
  lineHeight: 1.4
};

const updateSubmitIconStyle = {
  width: "22px",
  height: "22px",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round"
};

const emptyStyle = {
  padding: "40px",
  textAlign: "center",
  color: "#9ca3af"
};

const errorStyle = {
  padding: "18px",
  borderRadius: "8px",
  backgroundColor: "rgba(213, 23, 23, 0.15)",
  border: "1px solid rgba(213, 23, 23, 0.45)",
  color: "#ff9b9b",
  fontWeight: 600
};

export default ClientQueries;
